import { format } from "date-fns";

import { EmailSource, LastEmailOutcome, emailSourceGroup } from "@signalist/shared-types";

/**
 * What the email hover knows about the stored address beyond the address itself (PREC-4262). The
 * source and the verification are the caller's company's own facts about the current address; the
 * API sends null when it has none (or only about an older address).
 */
export interface EmailProvenance {
	emailSource?: string | null;
	emailSetAt?: string | Date | null;
	emailVerifiedAt?: string | Date | null;
	emailVerificationResult?: string | null;
	lastEmailOutcome?: LastEmailOutcome | null;
}

export type EmailProvenanceKind = "source" | "verified" | "lastSent";

export interface EmailProvenanceLine {
	kind: EmailProvenanceKind;
	label: string;
	value: string;
	/** A bounce reads as a warning; every other line is plain. */
	warning?: boolean;
}

/** The translator the lines are built with (`t` from `useTranslation`, or `i18n.t`). */
export type ProvenanceTranslate = (key: string) => string;

const KEY = "contacts.emailProvenance";

/** Verifier answers with a label of their own; any other answer reads as "Checked". */
export const LABELLED_VERIFICATION_RESULTS = ["valid", "invalid", "catch_all", "unknown"] as const;

/** A date as the hover shows it (dd.MM.yyyy), or null when there is none or it does not parse. */
export const formatProvenanceDate = (value: string | Date | null | undefined): string | null => {
	if (!value) {
		return null;
	}

	const date = new Date(value);

	return Number.isNaN(date.getTime()) ? null : format(date, "dd.MM.yyyy");
};

/**
 * A verifier's raw answer (`catch_all`) as its label ("Catch-all"). The raw value is a provider's
 * program value, so one without a key of its own reads as the neutral "Checked", never as itself.
 */
export const verificationLabel = (
	raw: string | null | undefined,
	t: ProvenanceTranslate
): string => {
	const known = LABELLED_VERIFICATION_RESULTS.find((result) => result === raw?.toLowerCase());

	return t(`${KEY}.verification.${known ?? "checked"}`);
};

const joinWithDate = (text: string, date: string | null): string =>
	date ? `${text} · ${date}` : text;

/** Signalist's own find; a user's typed address ("Added manually"); anything else they brought. */
const sourceText = (data: EmailProvenance, t: ProvenanceTranslate): string | null => {
	const group = emailSourceGroup(data.emailSource);

	if (group === "SIGNALIST") {
		return t(`${KEY}.source.signalist`);
	}

	if (group !== "USER_PROVIDED") {
		return null;
	}

	const text =
		data.emailSource === EmailSource.MANUAL
			? t(`${KEY}.source.manual`)
			: t(`${KEY}.source.userProvided`);

	return joinWithDate(text, formatProvenanceDate(data.emailSetAt));
};

const sourceLine = (data: EmailProvenance, t: ProvenanceTranslate): EmailProvenanceLine | null => {
	const value = sourceText(data, t);

	return value ? { kind: "source", label: t(`${KEY}.labels.source`), value } : null;
};

/** Shown only with both a date and an answer: a date alone would claim a check nobody can name. */
const verifiedLine = (
	data: EmailProvenance,
	t: ProvenanceTranslate
): EmailProvenanceLine | null => {
	const date = formatProvenanceDate(data.emailVerifiedAt);

	return date && data.emailVerificationResult
		? {
				kind: "verified",
				label: t(`${KEY}.labels.verified`),
				value: joinWithDate(verificationLabel(data.emailVerificationResult, t), date),
			}
		: null;
};

const lastSentLine = (
	data: EmailProvenance,
	t: ProvenanceTranslate
): EmailProvenanceLine | null => {
	const outcome = data.lastEmailOutcome;

	if (!outcome) {
		return null;
	}

	return {
		kind: "lastSent",
		label: t(`${KEY}.labels.lastSent`),
		value: joinWithDate(t(`${KEY}.outcome.${outcome.outcome}`), formatProvenanceDate(outcome.at)),
		warning: outcome.outcome === "BOUNCED",
	};
};

/**
 * The hover's provenance lines, in order: where the address came from, its last verification and
 * the lead's last email. A line without data is left out, never shown empty: an unknown source
 * (addresses stored before PREC-4262, or another company's), an address never verified (or a date
 * with no verifier answer), a lead never mailed.
 */
export const emailProvenanceLines = (
	data: EmailProvenance,
	t: ProvenanceTranslate
): EmailProvenanceLine[] =>
	[sourceLine(data, t), verifiedLine(data, t), lastSentLine(data, t)].filter(
		(line): line is EmailProvenanceLine => line !== null
	);
